// src/pages/Resources/SupportRequestModal.tsx
import "./SupportRequestModal.scss";
import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { FaLifeRing } from 'react-icons/fa';

interface Props {
  show: boolean;
  groupName: string;
  groupId: string;
  groupUrl?: string | null;
  userDisplayName?: string;
  userEmail?: string;
  onClose: () => void;
  onSubmit: (message: string) => Promise<void>;
}

const SupportRequestModal: React.FC<Props> = ({
  show,
  groupName,
  groupId,
  groupUrl,
  userDisplayName,
  userEmail,
  onClose,
  onSubmit
}) => {
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    if (!submitting) {
      setMessage('');
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    setSubmitting(true);
    try {
      await onSubmit(message);
      setMessage('');
      onClose();
    } catch (error) {
      // Error handled by parent
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton={!submitting}>
        <Modal.Title>
          <FaLifeRing className="me-2 text-primary" />
          Request Support
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="alert alert-info small mb-3">
            <strong>Note:</strong> This will send an email to the Service Desk. They will
            create a ticket and contact you shortly.
          </div>

          {/* Pre-filled Information */}
          <div className="mb-4">
            <h6 className="text-muted mb-2">Group Information</h6>
            <div className="p-3 border rounded bg-light">
              <div className="row mb-2">
                <div className="col-4 fw-semibold">Group Name:</div>
                <div className="col-8">{groupName}</div>
              </div>
              <div className="row mb-2">
                <div className="col-4 fw-semibold">Group ID:</div>
                <div className="col-8">
                  <code className="small">{groupId}</code>
                </div>
              </div>
              {groupUrl && (
                <div className="row">
                  <div className="col-4 fw-semibold">Group URL:</div>
                  <div className="col-8 small">
                    <a href={groupUrl} target="_blank" rel="noreferrer">
                      {groupUrl}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mb-4">
            <h6 className="text-muted mb-2">Your Information</h6>
            <div className="p-3 border rounded bg-light">
              <div className="row mb-2">
                <div className="col-4 fw-semibold">Name:</div>
                <div className="col-8">{userDisplayName || '—'}</div>
              </div>
              <div className="row">
                <div className="col-4 fw-semibold">Email:</div>
                <div className="col-8">{userEmail || '—'}</div>
              </div>
            </div>
          </div>

          {/* Message */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              Describe your issue or request: <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Please provide details about what you need help with..."
              required
              disabled={submitting}
              autoFocus
            />
            <Form.Text className="text-muted">
              Please be as specific as possible to help us resolve your issue quickly.
            </Form.Text>
          </Form.Group>

          <div className="alert alert-secondary small mb-0">
            <strong>What happens next?</strong>
            <ol className="mb-0 mt-2 ps-3">
              <li>Your request will be sent to the Service Desk</li>
              <li>A support ticket will be created and assigned</li>
              <li>You'll receive a confirmation email with the ticket number</li>
            </ol>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={!message.trim() || submitting}
          >
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Sending...
              </>
            ) : (
              <>
                <FaLifeRing className="me-2" />
                Submit Request
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default SupportRequestModal;
